import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import CommentListing from "components/CommentListing/CommentListing";
import FiveStartIconForRate from "components/FiveStartIconForRate/FiveStartIconForRate";
import StartRating from "components/StartRating/StartRating";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import { useParams, Link } from "react-router-dom";
import { Dialog, Popover, Transition } from "@headlessui/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import ButtonCircle from "shared/Button/ButtonCircle";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DetailPagetLayout from "../Common/Layout";
import useWindowSize from "application/hooks/useWindowResize";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import { useNavigate } from "react-router-dom";
import {
  useGetPropertyQuery,
  useGetPropertyStatsQuery,
  useLazyGetPropertyAvailabilityByDatesQuery,
  useLazyGetPropertyAvailabilityByRangeQuery,
} from "application/features/property/propertyApiSlice";
import {
  useLazyGetAllHolidayPackageQuery,
} from "application/features/holidayPackage/holidayPackageApiSlice";
import Skeleton from "react-loading-skeleton";
import MobileFooterSticky from "../Common/MobileFooterSticky";
import PackagesForStayModal from "components/Quasi/PackagesForStayModal";
import LocationsMap from "components/GoogleMaps/LocationsMap";
import moment from "moment";
import { GuestsObject } from "data/interfaces";
import InputBar from "components/InputBar/InputBar";
import { useDispatch } from "react-redux";
import {
  selectRange,
  setBooking,
} from "application/features/bookings/bookingsSlice";
import { selectCheckIn, selectCheckOut } from "application/features/search/searchSlice";
import {
  useLazyGetExperiencesNearbyQuery,
} from "application/features/experience/experienceApiSlice";
import { useSelector } from "react-redux";
import {
  selectExperiencesList,
  setExperiencesList,
} from "application/features/experience/experienceSlice";
import ModalLogin from "../Common/ModalLogin";
import { selectCurrentclient } from "application/features/client/clientSlice";
import { selectCurrentToken } from "application/features/clientAuth/authClientSlice";
import {
  selectAvailability,
  setAvailability,
  setProperty,
} from "application/features/property/propertySlice";
import { setEvents } from "application/features/calendar/calendarSlice";
import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { toast } from "react-toastify";
import {
  useGetReviewByPropertyUUIDQuery,
  useLazyGetReviewByPropertyUUIDQuery,
} from "application/features/review/reviewApiSlice";
import { selectGuests } from "application/features/guests/guestsSlice";
import Pagination from "shared/Pagination/Pagination";
import returnMoodColor from "shared/AppFunctions/returnMoodColor";
import type { BookingDataType } from "data/interfaces";
import { StockEnum, MoodEnum, StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import LikeSaveBtns from "components/LikeSaveBtns";
import ImageMosaicGallery from "components/Quasi/ImageMosaicGallery/ImageMosaicGallery";
import AmenitiesTag from "components/Quasi/AmenitiesTag/AmenitiesTag";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import StayDatesRangeInputWrapper from "containers/ListingDetailPages/Common/StayDatesRangeInputWrapper";
import GuestsInput from "containers/ListingDetailPages/Common/GuestsInput";
import {
  formatPrice,
  calculatePriceNumeric,
} from "shared/AppFunctions/formatPrice";
import { useTranslation } from "react-i18next";

interface StayDetailsPageProps {
  radius?: number;
}

const StayDetailsPageContainer: FC<StayDetailsPageProps> = ({
  radius = 25,
}) => {
  // STATES
  const searchCheckIn = useSelector(selectCheckIn);
  const searchCheckOut = useSelector(selectCheckOut);
  const [startDate, setStartDate] = useState<Date>(
    searchCheckIn ? moment(searchCheckIn).toDate() : moment().add(1, "day").toDate(),
  );
  const [endDate, setEndDate] = useState<Date>(
    searchCheckOut ? moment(searchCheckOut).toDate() : moment().add(3, "day").toDate(),
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showFooterSticky, setShowFooterSticky] = useState(true);

  const guests: GuestsObject = useSelector(selectGuests);

  const [isOpenModalAmenities, setIsOpenModalAmenities] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isImageFullScreen, setIsImageFullScreen] = useState("");

  // PARAMS
  const id = useParams().id;

  // HOOKS
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, ready } = useTranslation("translation");

  const { width, height } = useWindowSize();

  // QUERIES
  const {
    data: listing,
    isLoading,
    isSuccess,
    isError,
  } = useGetPropertyQuery(id);
  // `id` may be a slug or a uuid; the canonical uuid comes from the loaded
  // listing and drives the uuid-based secondary queries (stats, reviews).
  const propertyUuid = String(listing?.property?.property_uuid ?? "");
  const [
    fetchReviews,
    { data: reviews, isLoading: isLoadingReviews, isSuccess: isSuccessReviews },
  ] = useLazyGetReviewByPropertyUUIDQuery();
  const [
    fetchExperiencesNearby,
    {
      data: experiencesNearby,
      isLoading: isLoadingExperiences,
      isSuccess: isSuccessExperiencesNearby,
    },
  ] = useLazyGetExperiencesNearbyQuery({});
  const { data: propertyStats, isLoading: isLoadingStats } =
    useGetPropertyStatsQuery(propertyUuid, { skip: !propertyUuid });
  const [
    checkIfPropertyIsAvailable,
    { data: propertyIsAvailable, isLoading: isCheckingPropertyAvailability },
  ] = useLazyGetPropertyAvailabilityByDatesQuery({});
  const [
    checkAvailabilityInRange,
    { data: rangeAvailability, isLoading: isLoadingRangeAvailability },
  ] = useLazyGetPropertyAvailabilityByRangeQuery({});
  const [
    triggerAllPackages,
    { data: allPackages, isFetching: isLoadingPackages },
  ] = useLazyGetAllHolidayPackageQuery();

  const packagesForProperty = useMemo(() => {
    if (!allPackages || !propertyUuid) return [];
    return (allPackages as any[]).filter((pkg) =>
      (pkg?.package_content ?? []).some(
        (it: any) => it?.type === "property" && it?.uuid === propertyUuid
      )
    );
  }, [allPackages, propertyUuid]);

  useEffect(() => {
    if (listing?.property?.only_in_package) triggerAllPackages(undefined);
  }, [listing?.property?.only_in_package, triggerAllPackages]);

  // SELECTORS
  const experiencesList = useSelector(selectExperiencesList);
  const client_token = useSelector(selectCurrentToken);
  const range = useSelector(selectRange);

  // FETCH FUNCTIONS
  const handleFetchExperiences = async () => {
    const latitude = listing.property?.latitude;
    const longitude = listing.property?.longitude;
    if (latitude == null || longitude == null) {
      // No geographic anchor on this property; skip the nearby lookup.
      return;
    }
    try {
      const res = await fetchExperiencesNearby({
        latitude,
        longitude,
        radius: radius,
      }).unwrap();

      // Add href field to each experience for navigation
      const experiencesWithHref = res.map((experience: any) => ({
        ...experience,
        href: stockDetailPath(StockNumericType.EXPERIENCE, experience.slug || experience.experience_uuid),
      }));

      dispatch(setExperiencesList(experiencesWithHref));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (propertyUuid) fetchReviews({ property_uuid: propertyUuid, page: 1 });
  }, [propertyUuid]);

  const handleStartBooking = async (skipClientControl: boolean) => {
    // set booking data to redux
    if (!client_token && !skipClientControl) {
      setShowLoginModal(true);
    } else {
      if (listing.property?.only_in_package) {
        toast.info("Questa struttura è prenotabile solo all'interno di un pacchetto");
        return;
      }
      setShowLoginModal(false);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
      const totalGuests =
        (guests.adults || 0) + (guests.children || 0) + (guests.infants || 0);
      if (totalGuests > listing.property?.guests_included) {
        toast.error(
          `Questo alloggio può ospitare al massimo ${listing.property?.guests_included} ospiti`,
        );
        return;
      }
      const weekday_price = Number(listing.property?.weekday_price);
      const weekend_price = Number(listing.property?.weekend_price);
      const cleaning_fee_per_night =
        Number(listing.property?.cleaning_fee) || 0;
      const totalDays = moment(endDate).diff(moment(startDate), "days");
      let price = 0;
      for (let i = 0; i < totalDays; i++) {
        const day = moment(startDate).add(i, "days").get("day");
        // if day is sunday or satuday
        if (day === 6 || day === 0) {
          price += weekend_price;
        } else {
          price += weekday_price;
        }
      }

      // Store raw base price and keep additional fees separate.
      // UI components such as MultiStockCard will apply commission formatting once.
      const total_cleaning_fee = cleaning_fee_per_night * totalDays;

      const bookingData: BookingDataType = {
        id: `${listing.property?.property_uuid}-${moment().format("YYYYMMDDHHmmss")}`,
        item: listing.property,
        type: StockEnum.STAY,
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        guests: totalGuests,
        price,
        additional_fees: total_cleaning_fee,
      };
      dispatch(setBooking(bookingData));
      setShowFooterSticky(false);
      toast.success("Prenotazione aggiunta al tuo viaggio");
    }
  };

  const handleFetchAvailability = async () => {
    try {
      const availability = await checkIfPropertyIsAvailable({
        property_uuid: listing.property.property_uuid,
        check_in: moment(startDate).format("YYYY-MM-DD"),
        check_out: moment(endDate).format("YYYY-MM-DD"),
      }).unwrap();
    } catch (error) {}
  };

  const handleFetchAvailabilityInRangeByInitialDate = async () => {
    try {
      // const today = moment().format("YYYY-MM-DD");
      const month = moment().format("MM");
      const year = moment().format("YYYY");
      const nextMonth = moment().add(1, "month").format("MM");

      const response = await checkAvailabilityInRange({
        property_uuid: listing.property.property_uuid,
        start_month: month,
        end_month: nextMonth,
        year: year,
      }).unwrap();

      dispatch(setAvailability(response));
    } catch (error) {}
  };

  // EVENT HANDLERS
  const onMonthChange = useCallback(
    async (date: Date) => {
      try {
        // get month and next month from date
        const month = moment(date).format("MM");
        const year = moment(date).format("YYYY");
        const nextMonth = moment(date).add(1, "month").format("MM");

        const response = await checkAvailabilityInRange({
          property_uuid: listing.property.property_uuid,
          start_month: month,
          end_month: nextMonth,
          year: year,
        }).unwrap();

        dispatch(setAvailability(response));
      } catch (error) {}
    },
    [
      /* dipendenze */
    ],
  );

  const onChangeGuests = (guests: any) => {
    const maxGuests = listing.property?.guests_included || 1;
    if (guests.guestAdults + guests.guestChildren > maxGuests) {
      alert(`Questo alloggio può ospitare al massimo ${maxGuests} ospiti`);
    }
    // setGuests(guests);
  };

  function closeModalAmenities() {
    setIsOpenModalAmenities(false);
  }

  function openModalAmenities() {
    setIsOpenModalAmenities(true);
  }

  // EFFECTS
  useEffect(() => {
    handleFetchAvailabilityInRangeByInitialDate();
  }, [listing?.property?.property_uuid]);

  useEffect(() => {
    //dispatch(setBookings([]));
  }, []);

  useEffect(() => {
    if (listing && isSuccess && !isLoading && !isSuccessExperiencesNearby) {
      handleFetchExperiences();
    }
    if (!isLoading && isSuccess) {
      dispatch(setProperty(listing.property));
    }
  }, [isSuccess]);

  useEffect(() => {
    if (listing && listing.property) {
      document.title = listing.property.name;
    }
  }, [listing]);

  useEffect(() => {
    // if listing is undefined return then navigate to not found page
    if (isError && !isLoading) {
      navigate("/404");
    }
  }, [isError, isLoading]);

  useEffect(() => {
    if (listing?.property?.in_review) {
      navigate("/in-review", {
        state: { type: "stay", name: listing.property.name },
        replace: true,
      });
    }
  }, [listing]);

  // NEW COMPONENT FUNCTIONS FOR SIDEBAR LAYOUT
  const handleOpenModalImageGallery = () => {
    // router(`${thisPathname}/?modal=PHOTO_TOUR_SCROLLABLE`);
  };

  const handleSave = (property_uuid: string) => {
    toast.info("Funzionalità in arrivo");
  };

  const handleShare = (property_uuid: string) => {
    toast.info("Funzionalità in arrivo");
  };

  const primaryInfoSection = () => {
    return (
      <div className="listingSection__wrap !space-y-6">
        {/* 1 */}
        <div className="flex justify-between items-center">
          <Badge
            color={returnMoodColor(listing?.property?.mood as MoodEnum)}
            name={listing?.property?.mood || ""}
          />
          <LikeSaveBtns
            actionSave={() => handleSave(listing?.property?.property_uuid)}
            actionShare={() => handleShare(listing?.property?.property_uuid)}
          />
        </div>

        {/* 2 */}
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          {listing?.property?.name || "No title"}
        </h2>

        {/* 3 */}
        <div className="flex items-center space-x-4">
          <StartRating
            point={listing?.property?.rating || 0}
            reviewCount={listing?.property?.reviews || 0}
          />
          <span>
            <i className="las la-map-marker-alt"></i>
            <span className="ml-1">
              {listing?.property?.address || "No address"}
            </span>
          </span>
        </div>

        {/* 4 */}
        <div className="flex items-center">
          <Avatar
            hasChecked
            sizeClass="h-10 w-10"
            radius="rounded-full"
            imgUrl={listing?.host?.avatar || ""}
          />
          <span className="ml-2.5 text-neutral-500 dark:text-neutral-400">
            {`Hosted by `}
            <span className="text-neutral-900 dark:text-neutral-200 font-medium">
              {listing?.host?.name || "Host Unknown"}
            </span>
          </span>
        </div>

        {/* 5 */}
        <div className="w-full border-b border-neutral-100 dark:border-neutral-700" />

        {/* 6 */}
        <div className="flex items-center justify-between xl:justify-start space-x-8 xl:space-x-12 text-sm text-neutral-700 dark:text-neutral-300">
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-user text-2xl"></i>
            <span className="">
              {listing?.property?.guests_included || 0} Guests
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-bed text-2xl"></i>
            <span className="">
              {listing?.property?.bedrooms || 0} Bedrooms
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-bath text-2xl"></i>
            <span className="">
              {listing?.property?.bathrooms || 0} Bathrooms
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-door-open text-2xl"></i>
            <span className="">{listing?.property?.beds || 0} Beds</span>
          </div>
        </div>
      </div>
    );
  };

  const bookingActionSidebar = () => {
    const totalGuests =
      (guests.adults || 0) + (guests.children || 0) + (guests.infants || 0);
    const weekday_price = Number(listing?.property?.weekday_price || 0);
    const weekend_price = Number(listing?.property?.weekend_price || 0);
    const cleaning_fee_per_night = Number(listing?.property?.cleaning_fee) || 0;
    const totalDays = moment(endDate).diff(moment(startDate), "days");

    let price = 0;
    for (let i = 0; i < totalDays; i++) {
      const day = moment(startDate).add(i, "days").get("day");
      if (day === 6 || day === 0) {
        price += weekend_price;
      } else {
        price += weekday_price;
      }
    }

    const total_cleaning_fee = cleaning_fee_per_night * totalDays;
    const finalPrice = calculatePriceNumeric(price + total_cleaning_fee, true);
    const totalFormattedPrice = formatPrice(
      price + total_cleaning_fee,
      true,
      window.navigator.language,
      "EUR",
    );

    return (
      <div className="listingSectionSidebar__wrap shadow-xl p-4 sm:p-6">
        {/* PRICE */}
        <div className="flex justify-between items-start sm:items-center mb-4 sm:mb-6">
          <div className="flex flex-col">
            <span className="text-2xl sm:text-3xl font-semibold leading-tight">
              {formatPrice(
                listing?.property?.weekday_price || 0,
                true,
                window.navigator.language,
                "EUR",
              )}
            </span>
            <span className="text-sm sm:text-base font-normal text-neutral-500 dark:text-neutral-400">
              /night
            </span>
          </div>
          <div className="mt-1 sm:mt-0">
            <StartRating
              point={listing?.property?.rating || 0}
              reviewCount={listing?.property?.reviews || 0}
            />
          </div>
        </div>

        {/* FORM */}
        <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-2xl sm:rounded-3xl mb-4 sm:mb-6">
          <StayDatesRangeInputWrapper
            className="flex-1 z-[11]"
            startDateParam={startDate}
            endDateParam={endDate}
            onChange={onChangeDate}
            onMonthChange={onMonthChange}
            show={showDatePicker}
            setShow={setShowDatePicker}
            rangeAvailability={rangeAvailability}
            freeSelection
          />
          <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex-1">
            <GuestsInput onChange={onChangeGuests} />
          </div>
        </form>

        {/* SUM */}
        <div className="flex flex-col space-y-3 sm:space-y-4 mb-4 sm:mb-6">
          {listing?.property?.cleaning_fee > 0 && (
            <>
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 text-sm sm:text-base">
                <span>{`Cleaning fee per night`}</span>
                <span>
                  {formatPrice(
                    listing?.property?.cleaning_fee || 0,
                    true,
                    window.navigator.language,
                    "EUR",
                  )}
                </span>
              </div>
              <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
            </>
          )}
          <div className="flex justify-between font-semibold text-base sm:text-lg">
            <span>Total</span>
            <span>{totalFormattedPrice}</span>
          </div>
        </div>

        {/* SUBMIT */}
        {listing?.property?.only_in_package ? (
          packagesForProperty.length === 0 && !isLoadingPackages ? (
            <ButtonPrimary
              disabled
              className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
            >
              Al momento non disponibile in pacchetti
            </ButtonPrimary>
          ) : isLoadingPackages ? (
            <Skeleton height={48} className="rounded-xl" />
          ) : packagesForProperty.length === 1 ? (
            <ButtonPrimary
              onClick={() => navigate(stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, packagesForProperty[0].slug || packagesForProperty[0].package_uuid))}
              className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
            >
              Vedi pacchetto
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              onClick={() => setShowPackagesModal(true)}
              className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
            >
              Vedi pacchetti ({packagesForProperty.length})
            </ButtonPrimary>
          )
        ) : (
          <ButtonPrimary
            onClick={() =>
              !showDatePicker
                ? handleStartBooking(false)
                : toast.error("Seleziona le date per continuare")
            }
            className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
            disabled={listing?.property?.stopped}
            loading={isCheckingPropertyAvailability || loading}
          >
            Add to Trip
          </ButtonPrimary>
        )}
      </div>
    );
  };

  const hostDetailsSection = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-2xl font-semibold">The Host</h2>
          <ButtonSecondary href={`/author/${listing?.property?.host_uuid}`}>
            Go to Host Profile
          </ButtonSecondary>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* host */}
        <div className="flex items-center space-x-4">
          <Avatar
            hasChecked
            hasCheckedClass="w-4 h-4 -top-0.5 right-0.5"
            sizeClass="h-14 w-14"
            radius="rounded-full"
            imgUrl={listing?.host?.avatar || ""}
          />
          <div>
            <Link className="block text-xl font-medium break-words" to={`/author/${listing?.property?.host_uuid}`}>
              {listing?.host?.name || ""}
            </Link>
            <div className="mt-1.5 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
              <StartRating
                point={listing?.host?.rating || 0}
                reviewCount={listing?.host?.reviews_count || 0}
              />
              <span className="mx-2">·</span>
              <span> 12 places</span>
            </div>
          </div>
        </div>

        {/* desc */}
        <span className="block text-neutral-6000 dark:text-neutral-300 break-words">
          {listing?.host?.description || ""}
        </span>

        {/* info */}
        <div className="block text-neutral-500 dark:text-neutral-400 space-y-2.5 break-words">
          {listing?.host?.created_at && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                Host TRIBYOU since{" "}
                {moment(listing?.host?.created_at).format("DD MMM YYYY")}
              </span>
            </div>
          )}
          <div className="flex items-center space-x-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2V8a2 2 0 01-2 2h-3l-4 4z"
              />
            </svg>
            <span>Response rate - 100%</span>
          </div>
          <div className="flex items-center space-x-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Responds quickly - in a few hours</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSection2 = () => {
    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Descrizione</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="text-neutral-6000 dark:text-neutral-300 max-w-full">
          <span className="max-w-full break-words">
            {listing.property?.description}
          </span>
        </div>
      </div>
    );
  };

  const renderSectionExperiences = () => {
    if (isLoadingExperiences) {
      return (
        <div className="listingSection__wrap">
          <h2 className="text-2xl font-semibold">Esperienze</h2>
          <div className="grid grid-cols-1 gap-6">
            {Array(4)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="w-full">
                  <Skeleton className="w-full h-[20rem]" />
                </div>
              ))}
          </div>
        </div>
      );
    }

    if (!experiencesList || experiencesList.length === 0) {
      return null;
    }

    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Esperienze</h2>
        <div className="grid grid-cols-1 gap-6">
          {experiencesList.map((item: any) => (
            <MultiStockCard
              key={item.experience_uuid}
              id={item.experience_uuid}
              title={item.name}
              address={item.address}
              cover={item.cover}
              images={[
                item.cover,
                ...(item.gallery_images || item.media || []),
              ].filter((url: any) => url)}
              annotation={item.type}
              href={item.href || stockDetailPath(StockNumericType.EXPERIENCE, item.slug || item.experience_uuid)}
              mood={item.mood as MoodEnum}
              price={item.price}
              orientation="horizontal"
            />
          ))}
        </div>
      </div>
    );
  };

  const renderSection3 = () => {
    return (
      <div className="listingSection__wrap">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">
          Servizi e comfort
        </h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4 sm:mb-6"></div>
        <div className="flex flex-wrap sm:flex lg:grid-cols-6 gap-3 sm:gap-4 text-sm text-neutral-700 dark:text-neutral-300">
          {listing.property?.amenities
            .filter((_: any, i: number) => i < 10)
            .map((item: string) => (
              <AmenitiesTag
                key={item}
                name={item}
                variant="compact"
                size="sm"
              />
            ))}
          {/* ----- */}
          {listing.property?.amenities.length > 10 && (
            <div className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-6">
              <ButtonSecondary
                onClick={openModalAmenities}
                className="w-full sm:w-auto"
              >
                Vedi altri {listing.property?.amenities.length - 10} servizi
              </ButtonSecondary>
            </div>
          )}
          {renderMotalAmenities()}
        </div>
      </div>
    );
  };

  const renderMotalAmenities = () => {
    return (
      <Transition appear show={isOpenModalAmenities} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeModalAmenities}
        >
          <div className="min-h-screen px-2 sm:px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block py-4 sm:py-8 my-4 h-auto w-full max-w-4xl max-h-[90vh]">
                <div className="inline-flex pb-2 flex-col w-full text-left align-middle transition-all transform overflow-hidden rounded-xl sm:rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 dark:text-neutral-100 shadow-xl h-full">
                  <div className="relative flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-200 dark:border-neutral-800">
                    <h3 className="text-base sm:text-lg font-medium leading-6 text-gray-900 dark:text-white text-center pr-8">
                      Servizi e comfort
                    </h3>
                    <span className="absolute left-2 sm:left-3 top-2 sm:top-3">
                      <ButtonClose onClick={closeModalAmenities} />
                    </span>
                  </div>
                  <div className="px-4 sm:px-6 overflow-auto text-neutral-700 dark:text-neutral-300 divide-y divide-neutral-200 dark:divide-neutral-700 z-[90] relative max-h-[60vh] sm:max-h-[70vh]">
                    {listing.property?.amenities.length > 0 ? (
                      <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 sm:gap-4 p-2 sm:p-4">
                        {listing.property?.amenities.map((item) => (
                          <div
                            key={item}
                            className="flex items-start sm:items-center p-3 sm:p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors text-left"
                          >
                            <AmenitiesTag
                              name={item}
                              variant="modal"
                              size="md"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-neutral-500 dark:text-neutral-400">
                          Nessun servizio disponibile
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    );
  };

  const renderSection6 = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">
          Recensioni ({reviews?.data?.length * reviews?.totalPages} recensioni)
        </h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* comment */}
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {reviews?.data?.map((item: any, index: number) => (
            <CommentListing key={index} data={item} />
          ))}
          <div className="pt-8 w-full flex items-center justify-center">
            <Pagination
              totalPages={reviews?.totalPages || 0}
              currentPage={reviews?.currentPage || 0}
              onPageChange={(page) => fetchReviews({ property_uuid: id, page })}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSection7 = () => {
    return (
      <div className="listingSection__wrap">
        <div className="w-full md:w-3/4">
          {/* HEADING */}
          <div>
            <h2 className="text-2xl font-semibold">Location</h2>
            <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
              {listing.property?.address} {listing.property?.building} ,
              {listing.property?.city}, {listing.property?.country}
            </span>
          </div>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mt-4" />

          {/* MAP */}
          <div className="aspect-w-5 mt-4 md:mt-4 aspect-h-5 sm:aspect-h-3 ring-1 ring-black/10 rounded-xl z-0">
            {listing.property?.latitude && listing.property?.longitude ? (
              <LocationsMap
                className=""
                defaultCenter={{
                  lat: Number(listing?.property?.latitude),
                  lng: Number(listing?.property?.longitude),
                }}
                markers={[
                  {
                    lat: Number(listing?.property?.latitude),
                    lng: Number(listing?.property?.longitude),
                  },
                ]}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-800">
                <span className="text-neutral-500 dark:text-neutral-400">
                  No map available
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection8 = () => {
    return (
      <div className="listingSection__wrap ">
        <div className="w-full space-y-2 md:w-3/4">
          {/* HEADING */}
          <h2 className='text-2xl font-semibold'>Politiche e condizioni</h2>
          <div className='w-14 border-b border-neutral-200 dark:border-neutral-700' />

          {/* CONTENT */}
          <div>
            <h4 className="text-lg font-semibold">Check-in e check-out</h4>
            <div className="mt-3 text-neutral-500 dark:text-neutral-400 max-w-md text-sm sm:text-base">
              <div className="flex space-x-10 justify-between p-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                <span>Check-in</span>
                <span>{listing?.property?.check_in}</span>
              </div>
              <div className="flex space-x-10 justify-between p-3">
                <span>Check-out</span>
                <span>{listing?.property?.check_out} </span>
              </div>
            </div>
          </div>
          {/* <div className='w-14 border-b border-neutral-200 dark:border-neutral-700' /> */}

          {/* CONTENT */}
          <div>
            <h4 className='text-lg font-semibold'>Cancellazione</h4>
            <span className='block mt-3 text-neutral-500 dark:text-neutral-400'>
              <Link to={"/terms-and-conditions"}>{t("detailPages.common.visitTermsAndConditions") || "Visita la pagina di termini e condizioni per maggiori informazioni."}</Link>
            </span>
          </div>

          {/* CONTENT */}
          <div>
            <h4 className="text-lg font-semibold">Cusr</h4>
            <div className="prose sm:prose">{listing?.property?.cusr}</div>
          </div>
        </div>
      </div>
    );
  };
  const Hero = () => {
    const containerStyle = {
      backgroundImage: `url(${listing.property?.cover_url || listing.property?.place_cover})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      display: "flex",
    };
    const { width, height } = useWindowSize();

    const renderSection5 = () => {
      return (
        <div className="md:w-full lg:w-2/3 px-6 py-8 flex flex-col space-y-3 bg-white rounded-lg">
          {/* HEADING */}
          <h2 className="text-2xl font-semibold hidden sm:block">
            Informazioni Host
          </h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

          {/* host */}
          <div className="flex items-center space-x-4">
            <Avatar
              imgUrl={listing.host?.avatar}
              hasChecked
              hasCheckedClass="w-4 h-4 -top-0.5 right-0.5"
              sizeClass="h-14 w-14"
              radius="rounded-full"
            />
            <div>
              <a className='block text-xl font-medium' href={`/author/${listing?.host_uuid}`}>
                {listing.host?.name}
              </a>
              <div className="mt-1.5 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
                <StartRating
                  reviewCount={listing.host?.reviews_count}
                  point={listing.host?.rating.toFixed(1)}
                />
                <span className="mx-2">·</span>
              </div>
            </div>
          </div>

          {/* desc */}
          <span className="block text-neutral-6000 dark:text-neutral-300 text-sm	">
            {listing.host?.description}
          </span>

          {/* info */}
          <div className="block text-neutral-500 dark:text-neutral-400 space-y-2.5">
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-sm">
                host TRIBYOU da{" "}
                {moment(listing.host?.created_at).format("DD MMM YYYY")}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              <span className="text-sm	">Tasso di risposta - 100%</span>
            </div>
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>

              <span className="text-sm">
                Risponde velocemente - in poche ore
              </span>
            </div>
          </div>

          {/* == */}
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex flex-row space-x-4">
            <ButtonSecondary href={"/author/" + listing?.host?.host_uuid}>
              Vai al profilo
            </ButtonSecondary>
            <ButtonSecondary href={"/client/chat"}>Chat</ButtonSecondary>
          </div>
        </div>
      );
    };

    return (
      <div
        className="hero-container relative h-96 md:h-[46rem] md:items-center md:justify-center items-center justify-start pl-5 md:pl-0"
        style={containerStyle}
      >
        <div className="absolute w-full h-full top-0 right-0 bg-[rgb(0,0,0,0.2)] z-[1]"></div>
        <section id="hero" className="z-[2]">
          <div className="container sm:space-x-3  lg:space-x-0 justify-center  xl:flex-row flex flex-col items-center md:pl-6 mx-auto space-y-12 md:flex-row">
            <div className="md:w-full lg:w-1/2 text-white mt-3 lg:mt-1 space-y-5">
              <div className="w-full flex flex-row">
                <div className="w-full sm:w-1/2"></div>

                <div className="text-white flex items-center  justify-between xl:justify-start space-x-8 xl:space-x-12 text-sm text-neutral-700 dark:text-neutral-300">
                  {/*
                               <div className='flex items-center space-x-1'>
                    <i className='las la-upload text-xl '></i>
                    <span className=' '>
                      <span className='font-normal text-xs'>Condividi</span>
                    </span>
                  </div>
                  <div className='flex items-center space-x-1'>
                    <i className='las la-heart text-xl'></i>
                    <span className=' '>
                      <span className='text-xs'>Salva</span>
                    </span>
                  </div>
               */}
                </div>
              </div>
              <Badge
                className="!text-[0.8rem] !p-2"
                name={listing?.property?.mood}
                color={returnMoodColor(listing?.property?.mood)}
              />
              <h1 className="text-4xl sm:text-4xl md:text-7xl lg:w-auto font-semibold">
                {listing.property?.name}
              </h1>
              <hr className="bg-white mb-[2rem] md:mb-0" />

              <div className="flex w-[90%] items-start md:items-center space-y-3 md:space-y-0 flex-col md:flex-row  flex-wrap justify-between xl:justify-start space-x-0 xl:space-x-12 text-sm text-white dark:text-neutral-300">
                <div className="flex items-center space-x-3 ">
                  <i className="las la-user text-xl"></i>
                  <span className=" font-normal text-md">
                    {listing.property?.guests_included} Ospiti
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="las la-bed text-xl"></i>
                  <span className="font-normal text-md">
                    {listing.property?.beds} camere da letto
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="las la-bath text-xl"></i>
                  <span className="font-normal text-md">
                    {listing.property?.bathrooms} bagni
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="las la-door-open text-xl"></i>
                  <span className="font-normal text-md">
                    {listing.property?.bedrooms} camere da letto
                  </span>
                </div>
              </div>
            </div>
            {width >= 768 && (
              <div className="w-full xl:w-1/2 flex flex-col items-center">
                {renderSection5()}
              </div>
            )}
          </div>
        </section>
      </div>
    );
  };

  const renderStatsSection = () => {
    const worksData = {
      labels: propertyStats?.works?.labels,
      datasets: [
        {
          data: propertyStats?.works?.counts,
          backgroundColor: [
            "rgba(247, 60, 105,0.9)",
            "rgba(54, 162, 235,0.9)",
            "rgba(255, 206, 86,0.9)",
            "rgba(75, 192, 192,0.9)",
            "rgba(153, 102, 255,0.9)",
            "rgba(255, 159, 64,0.9)",
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
    const agesData = {
      labels: propertyStats?.ages?.labels,
      datasets: [
        {
          label: "Anni di nascita",
          data: propertyStats?.ages?.counts,
          backgroundColor: [
            "rgba(247, 60, 105,0.9)",
            "rgba(54, 162, 235,0.9)",
            "rgba(255, 206, 86,0.9)",
            "rgba(75, 192, 192,0.9)",
            "rgba(153, 102, 255,0.9)",
            "rgba(255, 159, 64,0.9)",
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
    const citiesData = {
      labels: propertyStats?.cities?.labels,
      datasets: [
        {
          data: propertyStats?.cities?.counts,
          backgroundColor: [
            "rgba(247, 60, 105,0.9)",
            "rgba(54, 162, 235,0.9)",
            "rgba(255, 206, 86,0.9)",
            "rgba(75, 192, 192,0.9)",
            "rgba(153, 102, 255,0.9)",
            "rgba(255, 159, 64,0.9)",
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
    if (isLoadingStats) {
      return (
        <div className="w-full md:w-1/3 lg:w-1/4 p-2">
          <Skeleton className="w-full h-[20rem]" />
        </div>
      );
    }
    if (
      propertyStats.works.labels.length &&
      propertyStats.ages.labels.length &&
      propertyStats.cities.labels.length
    ) {
      return (
        <div className="listingSection__wrap">
          <h2 className="text-2xl font-semibold">La tribù:</h2>
          <p className="text-neutral-500 dark:text-neutral-400 !mt-2">
            Scopri chi sono i viaggiatori che hanno scelto questo posto e quali
            sono le loro caratteristiche
          </p>
          <div className="flex flex-row flex-wrap !mt-10 gap-6 md:gap-0">
            <div className="flex flex-col md:basis-[10%] basis-full">
              <div className="w-[13rem] mb-[1rem]">
                <Badge
                  className="!text-[0.8rem] !p-2"
                  name="Settore lavorativo"
                  color="red"
                />
              </div>
              <Doughnut data={worksData} />
            </div>
            {/* separator */}
            <div className="md:w-[1px] bg-gray-300 md:mx-[1rem] w-full h-[1px] md:h-auto"></div>
            {/* right */}
            <div className="md:basis-[38%] flex flex-col basis-full">
              <div className="w-[13rem] mb-[1rem]">
                <Badge
                  className="!text-[0.8rem] !p-2"
                  name="Età media"
                  color="green"
                />
              </div>
              <Bar data={agesData} />
            </div>
            {/* separator */}
            <div className="md:w-[1px] bg-gray-300 md:mx-[1rem] w-full h-[1px] md:h-auto"></div>
            {/* right */}
            <div className="flex flex-col md:basis-[10%] basis-full">
              <div className="w-[13rem] mb-[1rem]">
                <Badge
                  className="!text-[0.8rem] !p-2"
                  name="Provenienza"
                  color="blue"
                />
              </div>
              <Doughnut data={citiesData} />
            </div>
          </div>
        </div>
      );
    }
  };

  const Info = () => {
    if (!showLoginModal) {
      return (
        <div
          className={`listingSection__wrap mt-0 mb-[3rem] top-0 bg-white ${isOpenModalAmenities ? "z-0" : "z-[90]"} rounded-t-none `}
        >
          <div className="full flex flex-col md:flex-row space-x-4 lg:space-x-0 pr-4 md:pr-0 items-center justify-between w-[90%] self-center">
            <div className="w-auto flex flex-col my-3 md:my-0 pl-4">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold">
                €
                {listing.property.weekday_price
                  ? Number(listing.property.weekday_price) +
                    Number(listing.property.weekday_price * 0.3) +
                    Number(listing.property.weekday_price * 0.3) * 0.22
                  : "N/A"}
                <span className="text-xs sm:text-sm md:text-xs">/notte</span>
              </h1>
              {listing.property.cleaning_fee > 0 && (
                <div className="text-sm text-neutral-500 dark:text-neutral-400">
                  + €{listing.property.cleaning_fee} spese di pulizia per notte
                </div>
              )}
              <div className="flex items-center">
                <i className="las la-star text-xl sm:text-2xl text-rose-500"></i>
                <span className="">
                  <span className="font-normal text-xs sm:text-sm md:text-xs">
                    {listing.property.rating} ({listing.property.reviews}{" "}
                    recensioni)
                  </span>
                </span>
              </div>
            </div>

            <div className="w-auto text-neutral-500 dark:text-neutral-400">
              <div className="flex gap-4 flex-row items-center border border-slate-200 rounded-xl px-10 py-2">
                {
                  <InputBar
                    onMonthChange={onMonthChange}
                    startDate={startDate}
                    endDate={endDate}
                    onChangeDate={onChangeDate}
                    onChangeGuests={onChangeGuests}
                    maxGuests={listing.property?.guests_included}
                    showDatePicker={showDatePicker}
                    setShowDatePicker={setShowDatePicker}
                    rangeAvailability={rangeAvailability}
                  />
                }
              </div>
            </div>

            {!listing.property?.only_in_package && (
            <div className="flex flex-row items-center space-x-4">
              <div className="w-[15rem] md:text-right mt-4 md:mt-0">
                <ButtonPrimary
                  disabled={listing.property?.stopped}
                  onClick={() =>
                    !showDatePicker
                      ? handleStartBooking(false)
                      : toast.error("Seleziona le date per continuare")
                  }
                  loading={isCheckingPropertyAvailability || loading}
                  className="w-11/12"
                >
                  Aggiungi al mio viaggio
                </ButtonPrimary>
              </div>
            </div>
            )}
          </div>
        </div>
      );
    }
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (!start) return;

    // Durante la selezione del range, dopo il primo click end è ancora null:
    // imposta solo l'inizio e attendi la seconda data scelta dall'utente.
    if (!end) {
      setStartDate(start);
      return;
    }

    const startMonth = moment(start).format("MM");
    const endMonth = moment(end).format("MM");

    const startDay = moment(start).format("DD");
    const endDay = moment(end).format("DD");

    let isDateBooked = false;

    // Controllo se una data selezionata è già prenotata
    for (
      let month = parseInt(startMonth, 10);
      month <= parseInt(endMonth, 10);
      month++
    ) {
      const monthStr = month < 10 ? `0${month}` : `${month}`;
      const daysInMonth = rangeAvailability[monthStr];
      if (daysInMonth) {
        for (
          let day = parseInt(startDay, 10);
          day <= parseInt(endDay, 10);
          day++
        ) {
          const dayStr = day < 10 ? `0${day}` : `${day}`;
          if (daysInMonth.includes(dayStr)) {
            isDateBooked = true;
            break;
          }
        }
      }
      if (isDateBooked) break;
    }

    if (isDateBooked) {
      toast.error("Non puoi selezionare queste date perchè sono già prenotate");
    } else {
      setStartDate(start);
      setEndDate(end);
    }
  };

  if (!ready) {
    return null;
  }

  if (isLoading || !listing) {
    return (
      <main className="w-full overflow-hidden">
        <div className="w-full h-96">
          <Skeleton height={"100%"} width={"100%"} />
        </div>
        <div className="mt-5 pl-5">
          <Skeleton height={50} width={"80%"} />
        </div>
        <div className="mt-5 pl-5">
          <Skeleton height={200} width={"80%"} />
        </div>
      </main>
    );
  }

  if (listing?.property?.is_deleted) {
    return (
      <div className="container mt-11 mb-24 lg:mb-32">
        <div className="flex flex-col items-center justify-center space-y-8">
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
            Questo annuncio non è più disponibile
          </h2>
          <ButtonPrimary href="/listing-stay">Torna alla ricerca</ButtonPrimary>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SINGLE HEADER || Gallery */}
      <ImageMosaicGallery
        coverImage={listing?.property?.cover || ""}
        galleryImages={
          listing?.property?.gallery_images &&
          listing.property.gallery_images.length
            ? listing.property.gallery_images
            : listing?.property?.cover
              ? [listing.property.cover]
              : []
        }
        onOpenGallery={handleOpenModalImageGallery}
        alt="Stay Property"
      />

      <main className="relative z-10 my-6 sm:my-8 lg:my-11 flex flex-col lg:flex-row">
        <div className="flex flex-col px-3 sm:px-4 lg:flex-row max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto w-full">
          {/* CONTENT */}
          <div className="flex flex-col w-full space-y-6 sm:space-y-8 lg:w-3/5 xl:w-2/3 lg:pr-6 xl:pr-10 lg:space-y-8 xl:space-y-10">
            {primaryInfoSection()}
            <div className="block lg:hidden mb-8">{bookingActionSidebar()}</div>

            {renderSection2()}
            {renderSectionExperiences()}

            {renderSection3()}
            {renderSection7()}
            {renderStatsSection()}
            {renderSection8()}
          </div>

          {/* SIDEBAR */}
          <div className="hidden lg:block lg:w-2/5 xl:w-1/3 mt-14 lg:mt-0">
            <div className="sticky top-28 space-y-8">
              {bookingActionSidebar()}
              {hostDetailsSection()}
              {renderSection6()}
            </div>
          </div>
        </div>
      </main>
      <ModalLogin
        showModal={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => handleStartBooking(true)}
      />
      <PackagesForStayModal
        open={showPackagesModal}
        onClose={() => setShowPackagesModal(false)}
        packages={packagesForProperty}
        loading={isLoadingPackages}
        stayName={listing?.property?.name}
      />
      <Transition show={showFooterSticky} as={Fragment}>
        <Transition.Child>
          <MobileFooterSticky
            isAvailable={!listing.property?.only_in_package && !listing.property?.stopped}
            price={listing.property.weekday_price}
            rating={listing.property.rating}
            reviews={listing.property.reviews}
            showDateModal
            startDate={startDate}
            endDate={endDate}
            handleStartBooking={() => handleStartBooking(false)}
            onChangeDate={onChangeDate}
            rangeAvailability={rangeAvailability}
            label={listing.property?.only_in_package ? "Vedi pacchetti" : "Prenota"}
            onSeePackages={listing.property?.only_in_package && packagesForProperty.length > 0 ? () => navigate(stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, packagesForProperty[0].slug || packagesForProperty[0].package_uuid)) : undefined}
          />
        </Transition.Child>
      </Transition>
    </>
  );
};

export default function StayDetailsPage() {
  return (
    <DetailPagetLayout>
      <StayDetailsPageContainer />
    </DetailPagetLayout>
  );
}
